import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  Filter,
  Mail,
  MoreVertical,
  Phone,
  Search,
  Send,
  User,
  X,
  XCircle
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const Requests = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCandidateProfile, setShowCandidateProfile] = useState(false);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dateRange: '',
    sortBy: '-createdAt'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRequests: 0,
    limit: 12
  });

  useEffect(() => {
    fetchRequests();
  }, [filters, pagination.currentPage]);

  useEffect(() => {
    filterRequests();
  }, [requests, filters]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit,
        sort: filters.sortBy,
        ...(filters.status && { status: filters.status })
      });

      const token = localStorage.getItem('recruiter_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/requests?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setRequests(response.data.requests || []);
      setPagination({
        ...pagination,
        totalPages: response.data.totalPages || 1,
        totalRequests: response.data.total || 0
      });
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidateDetails = async (candidateId) => {
    try {
      setCandidateLoading(true);
      const token = localStorage.getItem('recruiter_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/${candidateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('🔍 FRONTEND - Candidate API Response:', response.data);

      if (response.data.success) {
        console.log('🔍 FRONTEND - Candidate Data:', response.data.data);
        console.log('🔍 FRONTEND - Has Email?', response.data.data.email !== null);
        console.log('🔍 FRONTEND - Has Phone?', response.data.data.phone !== null);
        setSelectedCandidate(response.data.data);
        setShowCandidateProfile(true);
      } else {
        toast.error('Failed to fetch candidate details');
      }
    } catch (error) {
      console.error('🔍 FRONTEND - Error fetching candidate details:', error);
      console.error('🔍 FRONTEND - Error response:', error.response?.data);
      toast.error('Failed to fetch candidate details');
    } finally {
      setCandidateLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...requests];

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(request => 
        request.message?.toLowerCase().includes(searchTerm) ||
        request.job?.title?.toLowerCase().includes(searchTerm) ||
        request.candidates?.some(candidate => 
          candidate.firstName?.toLowerCase().includes(searchTerm) ||
          candidate.lastName?.toLowerCase().includes(searchTerm) ||
          candidate.email?.toLowerCase().includes(searchTerm)
        )
      );
    }

    setFilteredRequests(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'partially-responded': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'sent': return <Send className="w-4 h-4" />;
      case 'partially-responded': return <Mail className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getResponseStats = (request) => {
    if (!request.candidates) return { total: 0, responded: 0, accepted: 0, rejected: 0 };
    
    const total = request.candidates.length;
    const responded = request.candidates.filter(c => c.response).length;
    const accepted = request.candidates.filter(c => c.response === 'accepted').length;
    const rejected = request.candidates.filter(c => c.response === 'rejected').length;
    
    return { total, responded, accepted, rejected };
  };

  // Helper to build a resume URL (handles legacy resumes without the url field)
  const getResumeUrl = (resume) => {
    if (!resume) return null;
    return resume.url || `${process.env.REACT_APP_API_URL}/api/upload/files/${resume.filename}`;
  };

  // Fetch resume with auth header and open in new tab or trigger download
  const fetchAndOpenResume = async (resume, download = false) => {
    const url = getResumeUrl(resume);
    if (!url) {
      toast.error('Resume URL not available');
      return;
    }

    try {
      const token = localStorage.getItem('recruiter_token');
      if (!token) {
        toast.error('Authentication token missing. Please login again.');
        return;
      }

      const response = await axios.get(url, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);

      if (download) {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = resume.originalName || 'resume.pdf';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        window.open(blobUrl, '_blank');
      }

      // Revoke after some time
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } catch (error) {
      console.error('Error fetching resume:', error);
      toast.error('Failed to fetch resume');
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('recruiter_token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/requests/${requestId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Request cancelled successfully');
      fetchRequests();
    } catch (error) {
      console.error('Error cancelling request:', error);
      toast.error('Failed to cancel request');
    }
  };

  const handleResendRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('recruiter_token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/requests/${requestId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Request resent successfully');
      fetchRequests();
    } catch (error) {
      console.error('Error resending request:', error);
      toast.error('Failed to resend request');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, currentPage: 1 });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      dateRange: '',
      sortBy: '-createdAt'
    });
  };

  const handlePageChange = (page) => {
    setPagination({ ...pagination, currentPage: page });
  };

  if (loading && requests.length === 0) {
    return (
      <div className="p-6 max-w-screen-2xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Bulk Requests</h1>
        <p className="text-gray-600">
          Track and manage your bulk candidate requests
        </p>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card mb-8"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by message, job title, or candidate..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>

          {/* Filter Controls */}
          <div className="flex flex-wrap gap-4">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input-field min-w-[140px]"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="partially-responded">Partially Responded</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="input-field min-w-[140px]"
            >
              <option value="-createdAt">Newest First</option>
              <option value="createdAt">Oldest First</option>
              <option value="status">Status</option>
              <option value="-updatedAt">Recently Updated</option>
            </select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Showing {filteredRequests.length} of {pagination.totalRequests} requests
          </p>
          <div className="flex items-center space-x-4">
            {(filters.search || filters.status) && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear Filters
              </button>
            )}
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">Filtered</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Requests Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
      >
        {filteredRequests.map((request, index) => {
          const stats = getResponseStats(request);
          
          return (
            <motion.div
              key={request._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              className="card hover:shadow-hover transition-all duration-300"
            >
              {/* Request Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`status-badge ${getStatusColor(request.status)} flex items-center`}>
                      {getStatusIcon(request.status)}
                      <span className="ml-1 capitalize">{request.status.replace('-', ' ')}</span>
                    </span>
                  </div>
                  
                  {request.job && (
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {request.job.title}
                    </h3>
                  )}
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(request.createdAt).toLocaleDateString()}
                  </div>
                </div>
                
                <div className="relative">
                  <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                    <MoreVertical className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Candidates Stats */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Candidates</span>
                  <span className="text-sm text-gray-600">{stats.total} total</span>
                </div>
                
                {/* <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center p-2 bg-blue-50 rounded">
                    <div className="font-medium text-blue-700">{stats.responded}</div>
                    <div className="text-blue-600">Responded</div>
                  </div>
                  <div className="text-center p-2 bg-green-50 rounded">
                    <div className="font-medium text-green-700">{stats.accepted}</div>
                    <div className="text-green-600">Accepted</div>
                  </div>
                  <div className="text-center p-2 bg-red-50 rounded">
                    <div className="font-medium text-red-700">{stats.rejected}</div>
                    <div className="text-red-600">Rejected</div>
                  </div>
                </div> */}
              </div>

              {/* Message Preview */}
              <div className="mb-4">
                <p className="text-sm text-gray-600 line-clamp-3">
                  {request.message}
                </p>
              </div>

              {/* Progress Bar */}
              {stats.total > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>Response Rate</span>
                    <span>{Math.round((stats.responded / stats.total) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(stats.responded / stats.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setSelectedRequest(request);
                    setShowDetails(true);
                  }}
                  className="btn-secondary text-sm py-2 px-4 flex items-center"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </button>

                <div className="flex items-center space-x-2">
                  {request.status === 'sent' && (
                    <button
                      onClick={() => handleResendRequest(request._id)}
                      className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50"
                      title="Resend Request"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  )}
                  
                  {(request.status === 'pending' || request.status === 'sent') && (
                    <button
                      onClick={() => handleCancelRequest(request._id)}
                      className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
                      title="Cancel Request"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Empty State */}
      {filteredRequests.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="empty-state"
        >
          <Send className="empty-state-icon" />
          <h3 className="empty-state-title">
            {filters.search || filters.status
              ? 'No requests found'
              : 'No bulk requests yet'
            }
          </h3>
          <p className="empty-state-description">
            {filters.search || filters.status
              ? 'Try adjusting your search criteria or filters'
              : 'Bulk requests will appear here when you send them to candidates'
            }
          </p>
          {(filters.search || filters.status) && (
            <button onClick={clearFilters} className="btn-secondary">
              Clear Filters
            </button>
          )}
        </motion.div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="pagination"
        >
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className="pagination-item disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {[...Array(pagination.totalPages)].map((_, index) => {
            const page = index + 1;
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`pagination-item ${
                  pagination.currentPage === page ? 'active' : ''
                }`}
              >
                {page}
              </button>
            );
          })}
          
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
            className="pagination-item disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </motion.div>
      )}

      {/* Request Details Modal */}
      {showDetails && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowDetails(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal-content max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="text-xl font-semibold text-gray-900">
                Request Details
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-body space-y-6">
              {/* Request Info */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Request Information</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Status:</span>
                    <span className={`status-badge ${getStatusColor(selectedRequest.status)} flex items-center`}>
                      {getStatusIcon(selectedRequest.status)}
                      <span className="ml-1 capitalize">{selectedRequest.status.replace('-', ' ')}</span>
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Created:</span>
                    <span className="text-sm text-gray-600">
                      {new Date(selectedRequest.createdAt).toLocaleString()}
                    </span>
                  </div>
                  
                  {selectedRequest.job && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Job Position:</span>
                      <span className="text-sm text-gray-600">{selectedRequest.job.title}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Message */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Message</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedRequest.message}</p>
                </div>
              </div>

              {/* Candidates */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">
                  Candidates ({selectedRequest.candidates?.length || 0})
                </h4>
                
                {selectedRequest.candidates && selectedRequest.candidates.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {selectedRequest.candidates.map((candidate, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-700">
                              {(candidate.firstName?.charAt(0) || '').toUpperCase()}{(candidate.lastName?.charAt(0) || '').toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {selectedRequest.status === 'approved'
                                ? `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'Name not available'
                                : `${(candidate.firstName?.charAt(0) || '').toUpperCase()}${(candidate.lastName?.charAt(0) || '').toUpperCase()}`
                              }
                            </p>
                            <p className="text-sm text-gray-600">
                              {selectedRequest.status === 'approved' 
                                ? (candidate.email || 'Email not available') 
                                : (candidate.email ? `${candidate.email.substring(0, 3)}***@***.***` : 'Email hidden') 
                              }
                            </p>
                            {selectedRequest.status === 'approved' && candidate.phone && (
                              <p className="text-xs text-gray-500"> 
                                📞 {candidate.phone} 
                              </p> 
                            )}
                          </div>
                        </div>
                        
                        <div className="text-right">
                          {candidate.response ? (
                            <span className={`status-badge ${
                              candidate.response === 'accepted' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {candidate.response === 'accepted' ? 'Accepted' : 'Rejected'}
                            </span>
                          ) : (
                            <button
                              className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Candidate Profile"
                              disabled={candidateLoading}
                              onClick={() => {
                                fetchCandidateDetails(candidate._id);
                              }}
                            >
                              {candidateLoading ? (
                                <div className="w-5 h-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </button>
                          )}
                          {candidate.respondedAt && (
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(candidate.respondedAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No candidates found</p>
                )}
              </div>

              {/* Response Summary */}
              {selectedRequest.candidates && selectedRequest.candidates.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Response Summary</h4>
                  <div className="grid grid-cols-4 gap-4">
                    {(() => {
                      const stats = getResponseStats(selectedRequest);
                      return (
                        <>
                          <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
                            <div className="text-sm text-blue-600">Total Sent</div>
                          </div>
                          <div className="text-center p-4 bg-yellow-50 rounded-lg">
                            <div className="text-2xl font-bold text-yellow-700">{stats.responded}</div>
                            <div className="text-sm text-yellow-600">Responded</div>
                          </div>
                          <div className="text-center p-4 bg-green-50 rounded-lg">
                            <div className="text-2xl font-bold text-green-700">{stats.accepted}</div>
                            <div className="text-sm text-green-600">Accepted</div>
                          </div>
                          <div className="text-center p-4 bg-red-50 rounded-lg">
                            <div className="text-2xl font-bold text-red-700">{stats.rejected}</div>
                            <div className="text-sm text-red-600">Rejected</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowDetails(false)}
                className="btn-secondary"
              >
                Close
              </button>
              <div className="flex items-center space-x-3">
                {selectedRequest.status === 'sent' && (
                  <button
                    onClick={() => {
                      handleResendRequest(selectedRequest._id);
                      setShowDetails(false);
                    }}
                    className="btn-secondary flex items-center"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Resend Request
                  </button>
                )}
                
                {(selectedRequest.status === 'pending' || selectedRequest.status === 'sent') && (
                  <button
                    onClick={() => {
                      handleCancelRequest(selectedRequest._id);
                      setShowDetails(false);
                    }}
                    className="btn-danger flex items-center"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel Request
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Candidate Profile Modal */}
      {showCandidateProfile && selectedCandidate && (
        <div className="modal-overlay" onClick={() => setShowCandidateProfile(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal-content max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="text-xl font-semibold text-gray-900">
                Candidate Profile
              </h3>
              <button
                onClick={() => setShowCandidateProfile(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-body space-y-6">
              {/* Permission Notice */}
              {selectedCandidate.email === null && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Limited Profile Access:</strong> Full contact details are only available for approved bulk requests or candidates who have applied to your jobs.
                  </p>
                </div>
              )}

              {/* Candidate Info */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Candidate Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {selectedCandidate.email !== null 
                          ? `${selectedCandidate.firstName} ${selectedCandidate.lastName}`
                          : `${selectedCandidate.firstName}${selectedCandidate.lastName}`
                        }
                      </p>
                      <p className="text-sm text-gray-600">
                        {selectedCandidate.email !== null ? 'Full Name' : 'Initials Only'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {selectedCandidate.email || 'Contact details hidden'}
                      </p>
                      <p className="text-sm text-gray-600">Email</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {selectedCandidate.phone || 'Contact details hidden'}
                      </p>
                      <p className="text-sm text-gray-600">Phone</p>
                    </div>
                  </div>

                  {selectedCandidate.currentJobTitle && (
                    <div className="flex items-center space-x-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{selectedCandidate.currentJobTitle}</p>
                        <p className="text-sm text-gray-600">Current Position</p>
                      </div>
                    </div>
                  )}

                  {selectedCandidate.totalExperience !== undefined && (
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{selectedCandidate.totalExperience} years</p>
                        <p className="text-sm text-gray-600">Total Experience</p>
                      </div>
                    </div>
                  )}

                  {selectedCandidate.address && (
                    <div className="flex items-center space-x-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">
                          {selectedCandidate.address.city}, {selectedCandidate.address.state}, {selectedCandidate.address.country}
                        </p>
                        <p className="text-sm text-gray-600">Location</p>
                      </div>
                    </div>
                  )}

                  {/* Additional fields for approved requests */}
                  {selectedCandidate.email !== null && selectedCandidate.dateOfBirth && (
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">
                          {new Date(selectedCandidate.dateOfBirth).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-600">Date of Birth</p>
                      </div>
                    </div>
                  )}

                  {selectedCandidate.email !== null && selectedCandidate.gender && (
                    <div className="flex items-center space-x-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{selectedCandidate.gender}</p>
                        <p className="text-sm text-gray-600">Gender</p>
                      </div>
                    </div>
                  )}

                  {selectedCandidate.email !== null && selectedCandidate.workAuthorization && (
                    <div className="flex items-center space-x-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{selectedCandidate.workAuthorization}</p>
                        <p className="text-sm text-gray-600">Work Authorization</p>
                      </div>
                    </div>
                  )}

                  {selectedCandidate.email !== null && selectedCandidate.expectedSalary && (
                    <div className="flex items-center space-x-3">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">
                          {selectedCandidate.expectedSalary.min && selectedCandidate.expectedSalary.max
                            ? `${selectedCandidate.expectedSalary.currency || 'USD'} ${selectedCandidate.expectedSalary.min}K - ${selectedCandidate.expectedSalary.max}K`
                            : selectedCandidate.expectedSalary.min
                            ? `${selectedCandidate.expectedSalary.currency || 'USD'} ${selectedCandidate.expectedSalary.min}K+`
                            : 'Not specified'
                          }
                        </p>
                        <p className="text-sm text-gray-600">Expected Salary</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Resume View - Only for approved requests */} 
              
              {/* Resume Download - Only for approved requests */}
              {selectedCandidate.email !== null && selectedCandidate.resume && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Resume</h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {selectedCandidate.resume.originalName || 'Resume.pdf'}
                          </p>
                          <p className="text-sm text-gray-600">
                            {selectedCandidate.resume.size ? `${Math.round(selectedCandidate.resume.size / 1024)} KB` : 'PDF Document'}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => fetchAndOpenResume(selectedCandidate.resume, false)}
                          className="btn-secondary text-sm py-2 px-4 flex items-center"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Resume
                        </button>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            fetchAndOpenResume(selectedCandidate.resume, true);
                          }}
                          className="btn-primary text-sm py-2 px-4 flex items-center"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download Resume
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Cover Letter - Only for approved requests */}
              {selectedCandidate.email !== null && selectedCandidate.coverLetter && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Cover Letter</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedCandidate.coverLetter}</p>
                  </div>
                </div>
              )}

              {/* Social Links - Only for approved requests */}
              {selectedCandidate.email !== null && selectedCandidate.socialLinks && Object.keys(selectedCandidate.socialLinks).some(key => selectedCandidate.socialLinks[key]) && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Social Links</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedCandidate.socialLinks.linkedin && (
                      <a
                        href={selectedCandidate.socialLinks.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                          <span className="text-white text-xs font-bold">in</span>
                        </div>
                        <span className="text-blue-700 font-medium">LinkedIn</span>
                      </a>
                    )}
                    {selectedCandidate.socialLinks.github && (
                      <a
                        href={selectedCandidate.socialLinks.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="w-6 h-6 bg-gray-800 rounded flex items-center justify-center">
                          <span className="text-white text-xs font-bold">GH</span>
                        </div>
                        <span className="text-gray-700 font-medium">GitHub</span>
                      </a>
                    )}
                    {selectedCandidate.socialLinks.portfolio && (
                      <a
                        href={selectedCandidate.socialLinks.portfolio}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                      >
                        <div className="w-6 h-6 bg-purple-600 rounded flex items-center justify-center">
                          <span className="text-white text-xs font-bold">P</span>
                        </div>
                        <span className="text-purple-700 font-medium">Portfolio</span>
                      </a>
                    )}
                    {selectedCandidate.socialLinks.twitter && (
                      <a
                        href={selectedCandidate.socialLinks.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <div className="w-6 h-6 bg-blue-400 rounded flex items-center justify-center">
                          <span className="text-white text-xs font-bold">T</span>
                        </div>
                        <span className="text-blue-700 font-medium">Twitter</span>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Languages - Only for approved requests */}
              {selectedCandidate.email !== null && selectedCandidate.languages && selectedCandidate.languages.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Languages</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedCandidate.languages.map((lang, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="font-medium text-gray-900">{lang.language}</span>
                        <span className="text-sm text-gray-600 px-2 py-1 bg-gray-200 rounded">
                          {lang.proficiency}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Job Preferences - Only for approved requests */}
              {selectedCandidate.email !== null && selectedCandidate.jobPreferences && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Job Preferences</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    {selectedCandidate.jobPreferences.preferredJobTypes && selectedCandidate.jobPreferences.preferredJobTypes.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">Preferred Job Types:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedCandidate.jobPreferences.preferredJobTypes.map((type, idx) => (
                            <span key={idx} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedCandidate.jobPreferences.preferredLocations && selectedCandidate.jobPreferences.preferredLocations.length > 0 && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">Preferred Locations:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedCandidate.jobPreferences.preferredLocations.map((location, idx) => (
                            <span key={idx} className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                              {location}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Remote Work:</span>
                      <span className={`text-sm px-2 py-1 rounded ${selectedCandidate.jobPreferences.remoteWork ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {selectedCandidate.jobPreferences.remoteWork ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Willing to Relocate:</span>
                      <span className={`text-sm px-2 py-1 rounded ${selectedCandidate.jobPreferences.willingToRelocate ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {selectedCandidate.jobPreferences.willingToRelocate ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Skills */}
              {selectedCandidate.skills && selectedCandidate.skills.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidate.skills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {skill.name}
                        {skill.level && (
                          <span className="ml-2 text-xs bg-blue-200 px-1 rounded">
                            {skill.level}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Limited Access Notice for Detailed Sections */}
              {selectedCandidate.email === null && (
                <div className="text-center py-8">
                  <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Request Contact Details for Full Profile
                  </h3>
                  <p className="text-gray-600 mb-4">
                    To view work experience, education, projects, and certifications, you need to request and get approval for contact details.
                  </p>
                </div>
              )}

              {/* Work Experience - Only show if full access */}
              {selectedCandidate.email !== null && selectedCandidate.workExperience && selectedCandidate.workExperience.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Work Experience</h4>
                  <div className="space-y-4">
                    {selectedCandidate.workExperience.map((exp, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h5 className="font-medium text-gray-900">{exp.jobTitle}</h5>
                            <p className="text-sm text-gray-600">{exp.company}</p>
                            {exp.location && (
                              <p className="text-sm text-gray-500">{exp.location}</p>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(exp.startDate).getFullYear()} - {exp.currentJob ? 'Present' : new Date(exp.endDate).getFullYear()}
                          </span>
                        </div>
                        {exp.description && (
                          <p className="text-sm text-gray-700 mt-2">{exp.description}</p>
                        )}
                        {exp.achievements && exp.achievements.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm font-medium text-gray-700 mb-1">Key Achievements:</p>
                            <ul className="text-sm text-gray-600 list-disc list-inside">
                              {exp.achievements.map((achievement, achIdx) => (
                                <li key={achIdx}>{achievement}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education - Only show if full access */}
              {selectedCandidate.email !== null && selectedCandidate.education && selectedCandidate.education.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Education</h4>
                  <div className="space-y-4">
                    {selectedCandidate.education.map((edu, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h5 className="font-medium text-gray-900">{edu.degree}</h5>
                            <p className="text-sm text-gray-600">{edu.institution}</p>
                            {edu.fieldOfStudy && (
                              <p className="text-sm text-gray-600">{edu.fieldOfStudy}</p>
                            )}
                          </div>
                          {edu.endDate && (
                            <span className="text-xs text-gray-500">
                              {new Date(edu.endDate).getFullYear()}
                            </span>
                          )}
                        </div>
                        {edu.grade && (
                          <p className="text-sm text-gray-700 mt-2">Grade: {edu.grade}</p>
                        )}
                        {edu.description && (
                          <p className="text-sm text-gray-700 mt-2">{edu.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Projects - Only show if full access */}
              {selectedCandidate.email !== null && selectedCandidate.projects && selectedCandidate.projects.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Projects</h4>
                  <div className="space-y-4">
                    {selectedCandidate.projects.map((project, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h5 className="font-medium text-gray-900">{project.name}</h5>
                          {project.endDate && (
                            <span className="text-xs text-gray-500">
                              {new Date(project.endDate).getFullYear()}
                            </span>
                          )}
                        </div>
                        {project.description && (
                          <p className="text-sm text-gray-700 mb-2">{project.description}</p>
                        )}
                        {project.technologies && project.technologies.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {project.technologies.map((tech, techIdx) => (
                              <span key={techIdx} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded">
                                {tech}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex space-x-4 mt-2">
                          {project.projectUrl && (
                            <a
                              href={project.projectUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 text-sm"
                            >
                              View Project
                            </a>
                          )}
                          {project.githubUrl && (
                            <a
                              href={project.githubUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-600 hover:text-gray-700 text-sm"
                            >
                              GitHub
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications - Only show if full access */}
              {selectedCandidate.email !== null && selectedCandidate.certifications && selectedCandidate.certifications.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Certifications</h4>
                  <div className="space-y-3">
                    {selectedCandidate.certifications.map((cert, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-medium text-gray-900">{cert.name}</h5>
                            {cert.issuingOrganization && (
                              <p className="text-sm text-gray-600">{cert.issuingOrganization}</p>
                            )}
                            {cert.credentialId && (
                              <p className="text-xs text-gray-500 mt-1">ID: {cert.credentialId}</p>
                            )}
                          </div>
                          <div className="text-right">
                            {cert.issueDate && (
                              <p className="text-xs text-gray-500">
                                Issued: {new Date(cert.issueDate).toLocaleDateString()}
                              </p>
                            )}
                            {cert.expirationDate && (
                              <p className="text-xs text-gray-500">
                                Expires: {new Date(cert.expirationDate).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        {cert.credentialUrl && (
                          <a
                            href={cert.credentialUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm mt-2 inline-block"
                          >
                            View Credential
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowCandidateProfile(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Requests; 